import LArtwork from "@components/LArtwork";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import LText from "@components/LText";
import { AccessTypeOptions } from "@libs/utils";
import { fonts, theme } from "@theme";
import dayjs from "dayjs";
import { useMemo } from "react";
import {
  getAccessImage,
  prefetchAccessDetailHeroImage,
} from "@utils/accesses/access";
import { Access } from "@hooks/trpc/useAccesses";
import { secondsToTime } from "@utils/index";
import AccessTypeChip from "@components/Accesses/components/AccessTypeChip";
import LPressable from "@components/LPressable";
import { router } from "expo-router";
import { MERCH_DOWNLOAD_WORDS, merchDeliveryNote, merchPriceLabel } from "@libs/platform-wire";
import { createMerchOrderRowModel } from "@libs/shared-ui";

type Props = {
  access: {
    id: string;
    image?: string | null;
    artistName: string;
    startDateTime?: string;
    city?: string;
    songTitle?: string;
    type: AccessTypeOptions;
  } & Access;
  /** A wallet row: a merch product the viewer ordered shows its order, and
   *  opens it. */
  wallet?: boolean;
};

const Location = ({
  access,
}: {
  access: Props["access"] & { type: "event" };
}) => {
  return (
    <LBox flexDirection="row" alignItems="center" gap="s1">
      <LIcon
        colors={{ color: theme.colors.artistSubtitleText }}
        name="location_pin"
        size={10}
      />
      <LText
        variant="Caption/C1/Regular"
        fontFamily={fonts.regular}
        color="artistSubtitleText"
      >
        {access.venue
          ? `${access.venue}, ${access.city}`
          : access.city || access.country}
      </LText>
    </LBox>
  );
};

const Date = ({ access }: { access: Props["access"] & { type: "event" } }) => {
  return (
    <LBox flexDirection="row" alignItems="center" gap="s1">
      <LIcon
        colors={{ color: theme.colors.artistSubtitleText }}
        name="calendar"
        size={10}
      />
      <LText
        variant="Caption/C1/Regular"
        fontFamily={fonts.regular}
        color="artistSubtitleText"
      >
        {/* Venue-local `{ date, time }` resolved server-side (2026-07-23
            convention): render literally; append the 24h time when present. */}
        {access.startsAtLocal.time
          ? `${dayjs(access.startsAtLocal.date).format("MMM DD")}, ${access.startsAtLocal.time}`
          : dayjs(access.startsAtLocal.date).format("MMM DD")}
      </LText>
    </LBox>
  );
};

const Duration = ({
  access,
}: {
  access: Props["access"] & { type: "music" };
}) => {
  const duration = access.songs.at(0)?.duration;
  if (!duration) return null;
  return (
    <LBox flexDirection="row" alignItems="center" gap="s1">
      <LIcon
        colors={{ color: theme.colors.artistSubtitleText }}
        name="clock"
        size={10}
      />
      <LText
        variant="Caption/C1/Regular"
        fontFamily={fonts.regular}
        color="artistSubtitleText"
      >
        {secondsToTime(duration)}
      </LText>
    </LBox>
  );
};

const Artist = ({
  access,
}: {
  access: Props["access"] & { type: "music" };
}) => {
  return (
    <LBox flexDirection="row" alignItems="center" gap="s1">
      <LIcon
        colors={{ color: theme.colors.artistSubtitleText }}
        name="access"
        size={10}
      />
      <LText
        variant="Caption/C1/Regular"
        fontFamily={fonts.regular}
        color="artistSubtitleText"
      >
        {access.artistName}
      </LText>
    </LBox>
  );
};

/** A merch product's price and UK delivery, as Link in Bio's merch cards
 *  say them: "+ local tax" after a price before its seller's tax. */
const MerchPrice = ({
  access,
}: {
  access: Props["access"] & { type: "merch" };
}) => {
  if (!access.price) return null;
  const rows = [
    {
      icon: "cash" as const,
      text: merchPriceLabel(access.price, null, access.taxAdded),
    },
    // "+ UK delivery" where the store quotes its own for each basket;
    // "Digital download" for one the store emails.
    access.download
      ? { icon: "mail" as const, text: MERCH_DOWNLOAD_WORDS.label }
      : {
          icon: "merch" as const,
          text: merchDeliveryNote(access.deliveryMinor, access.price.currency),
        },
  ];
  return (
    <>
      {rows.map((row) =>
        row ? (
          <LBox key={row.icon} flexDirection="row" alignItems="center" gap="s1">
            <LIcon
              colors={{ color: theme.colors.artistSubtitleText }}
              name={row.icon}
              size={10}
            />
            <LText
              variant="Caption/C1/Regular"
              fontFamily={fonts.regular}
              color="artistSubtitleText"
            >
              {row.text}
            </LText>
          </LBox>
        ) : null,
      )}
    </>
  );
};

/** A merch order in the wallet: what was bought and when, as the order
 *  screen says them (`createMerchOrderRowModel`). */
const MerchOrderLines = ({
  model,
}: {
  model: NonNullable<ReturnType<typeof createMerchOrderRowModel>>;
}) => (
  <>
    {[
      { icon: "merch" as const, text: model.items },
      { icon: "calendar" as const, text: model.ordered },
    ].map((row) => (
      <LBox key={row.icon} flexDirection="row" alignItems="center" gap="s1">
        <LIcon
          colors={{ color: theme.colors.artistSubtitleText }}
          name={row.icon}
          size={10}
        />
        <LText
          variant="Caption/C1/Regular"
          fontFamily={fonts.regular}
          color="artistSubtitleText"
          numberOfLines={1}
          style={{ flexShrink: 1 }}
        >
          {row.text}
        </LText>
      </LBox>
    ))}
  </>
);

const ExperienceListItem = ({ access, wallet = false }: Props) => {
  const titleText = useMemo(() => {
    if ((access.type === "event" || access.type === "merch") && access.title) {
      return access.title;
    }
    if (access.type === "music") {
      if (access.songs.at(0)?.title) {
        return access.songs.at(0)?.title;
      }
    }
  }, [access.type, access.title]);

  const merchOrder =
    wallet && access.type === "merch" && access.orders?.length
      ? createMerchOrderRowModel(access.orders, access.title)
      : null;

  return (
    <LPressable
      onPress={() => {
        if (merchOrder) {
          router.push({
            pathname: "/(app)/access/[accessId]/my-order",
            params: { accessId: access.id },
          });
          return;
        }
        prefetchAccessDetailHeroImage(access);
        router.push(`/access/${access.id}`);
      }}
      sentryEventName={merchOrder ? "AccessListItem.MerchOrder" : "AccessListItem"}
    >
      <LBox
        justifyContent="space-between"
        alignItems="center"
        flexDirection="row"
        gap="s2"
      >
        <LBox flexDirection="row" gap="s3" flex={1}>
          <LBox>
            <LArtwork
              size={64}
              url={getAccessImage(access)}
              borderRadius="m-l"
              imageSize="medium"
            />
            <AccessTypeChip access={access} />
          </LBox>
          <LBox justifyContent="center" flexShrink={1}>
            <LText
              variant="Body/B2/SemiBold"
              color="white"
              mb="s1"
              numberOfLines={1}
            >
              {titleText}
            </LText>
            {access.type === "event" && (
              <>
                <Date access={access} />
                <Location access={access} />
              </>
            )}
            {access.type === "music" && (
              <>
                <Duration access={access} />
                <Artist access={access} />
              </>
            )}
            {merchOrder ? (
              <MerchOrderLines model={merchOrder} />
            ) : access.type === "merch" ? (
              <MerchPrice access={access} />
            ) : null}
          </LBox>
        </LBox>
        <LIcon
          name="chevron"
          opacity={0.3}
          rotate={270}
          size={16}
          stroke="white"
        />
      </LBox>
    </LPressable>
  );
};

export default ExperienceListItem;
